import makeItemLookup from './utils/makeItemLookup';
import { generateMaterials } from './materials';
import { generateSinks } from './sinks';
import { generateSinkFastenings } from './sinkFastenings';
import { generateDrainerGrooves } from './drainerGrooves';
import { generateShapes } from './shapes';
import { generateEdgeOptionsWithFiltering } from './edges';
import { generateThicknesses } from './thicknesses';
import { generateItems } from './items';
import { generateWallPanels } from './wallPanels';
import { generateSplashbacks } from './splashbacks';
import { generateWallEdgingStripMaterials, generateWallEdgingStripCustomizableMaterials } from './wallEdgingStrip';
import { generateKitchenIslands } from './kitchenIslands';
import { generateOverhangs } from './overhangs';
import { generateKitchenIslandOverhangs } from './kitchenIslandOverhang';

export function generateOptions(businessRules, itemsData, country, language) {
  const items = generateItems(itemsData, businessRules);
  const thicknesses = generateThicknesses(businessRules);
  const itemLookup = makeItemLookup(items);
  const edges = generateEdgeOptionsWithFiltering(businessRules, itemLookup);
  const materials = generateMaterials(businessRules, items, thicknesses, country, language);
  const sinks = generateSinks(businessRules, items, materials);
  const sinkFastenings = generateSinkFastenings(businessRules.hideOverflowHoles);
  const drainerGrooves = generateDrainerGrooves();

  const shapes = generateShapes(businessRules, materials);  
  const noWallPanelMaterials = businessRules !== undefined && businessRules.noWallPanelMaterials !== undefined && businessRules.noWallPanelMaterials;
  const useDeCentralizedDoubleSidedCalculation = businessRules !== undefined && businessRules.useDeCentralizedDoubleSidedCalculation;
  const useTwoIntervallWallPanelCalculation = businessRules !== undefined && businessRules.useTwoIntervallWallPanelCalculation !== undefined && businessRules.useTwoIntervallWallPanelCalculation;
  const useTwoDigitGroupingForNumbers = businessRules !== undefined && businessRules.useTwoDigitGroupingForNumbers !== undefined && businessRules.useTwoDigitGroupingForNumbers;
  const useImperialUnitMeasurementsInTheUI = businessRules !== undefined && businessRules.useImperialUnitMeasurementsInTheUI !== undefined && businessRules.useImperialUnitMeasurementsInTheUI;
  const useFamilyPrice = businessRules !== undefined && businessRules.useFamilyPrice !== undefined && businessRules.useFamilyPrice;
  const useDualFamilyPrice = businessRules !== undefined && businessRules.useDualFamilyPrice !== undefined && businessRules.useDualFamilyPrice;
  const useCustomerSignature = businessRules !== undefined && businessRules.useCustomerSignature !== undefined && businessRules.useCustomerSignature;
  const useCoverPanels = businessRules !== undefined && businessRules.useCoverPanels !== undefined && businessRules.useCoverPanels;  

  const {
    includeOverhangs,
    options: overhangs,
    overhangDefaultSize,
    waterfallHeight,
    overhangLegRequirements,
  } = generateOverhangs(businessRules, includeOverhangs);

  let splashbacks = [];
  let wallPanels = [];
  let wallEdgingStrip = [];
  let wallEdgingStripCustomizable = [];

  const includeWallEdgingStrip = materials.some((material) => material.wallEdgingStrip);
  const includeWallEdgingStripCustomizable = materials.some((material) => material.wallEdgingStripCustomizable);

  if(!noWallPanelMaterials){
    splashbacks = generateSplashbacks(businessRules, includeWallEdgingStrip, includeWallEdgingStripCustomizable);
    wallPanels = generateWallPanels(businessRules, items);
    wallEdgingStrip = generateWallEdgingStripMaterials(businessRules, items);
    wallEdgingStripCustomizable = generateWallEdgingStripCustomizableMaterials(businessRules, items);
  }

  const kitchenIslands = generateKitchenIslands(businessRules);
  const kitchenIslandOverhangs = generateKitchenIslandOverhangs(businessRules);

  const { measurements, wallPanelMeasurements, wallEdgingStripCustomizableMeasurements, freestandingHobDefaultWidth } = businessRules;

  const underGluedByOperation = materials.filter((material) => material.operations.underGlued).length > 0;

  if (materials.length === 0 || materials[0] === null) {
    throw new Error('No articles for materials!');
  }

  if (!noWallPanelMaterials && wallPanels.length === 0) {
    throw new Error('No articles for wall panels!');
  }

  if (!noWallPanelMaterials && !wallPanelMeasurements) {
    throw new Error('No measurement limits for wall panels!');
  }

  if (includeOverhangs && overhangs.length <= 1) {
    throw new Error('No overhangs available');
  }

  return {
    measurements,
    wallPanelMeasurements,
    wallEdgingStripCustomizableMeasurements,
    shapes,
    materials,
    edges,
    thicknesses,
    sinks,
    sinkFastenings,
    drainerGrooves,
    wallPanels,
    wallEdgingStrip,
    wallEdgingStripCustomizable,
    splashbacks,
    kitchenIslands,
    overhangs,
    kitchenIslandOverhangs,
    configuration: {
      underGluedByOperation,
      overhangDefaultSize,
      waterfallHeight,
      includeOverhangs,
      freestandingHobDefaultWidth,
      overhangLegRequirements,
      useDeCentralizedDoubleSidedCalculation,
      useTwoIntervallWallPanelCalculation,
      useTwoDigitGroupingForNumbers,
      useImperialUnitMeasurementsInTheUI,
      noWallPanelMaterials,
      useFamilyPrice,
      useDualFamilyPrice,
      useCustomerSignature,
      useCoverPanels
    },
  };
}
